import type { components } from '@multaqa/api-client'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import type { GroupRow } from '@/lib/platform'

export type GroupUser = components['schemas']['GroupUserResource']
export type AddCompanyInput = components['schemas']['AddGroupCompanyRequest']
export type SupportSession = components['schemas']['PlatformSupportSessionResource']
export type GroupCompany = { id: string; name: Record<string, string> }

export type GroupSettingsFields = {
  presentation_currency: string
  intercompany_mirroring: boolean
  retention_days: number | null
  purge_grace_days: number | null
}

export type GroupSettingsGroup = GroupRow & Partial<GroupSettingsFields>

export type {
  GroupDefaults,
  GroupDefaultsUpdateInput,
  TemplateDepartment,
  TemplateItemInput,
  TemplatePosition,
} from './api-defaults'
export {
  useCreateTemplateDepartment,
  useCreateTemplatePosition,
  useDeleteTemplateDepartment,
  useDeleteTemplatePosition,
  useGroupDefaults,
  useTemplateDepartments,
  useTemplatePositions,
  useUpdateGroupDefaults,
  useUpdateTemplateDepartment,
  useUpdateTemplatePosition,
} from './api-defaults'
export type { GroupEntitlementRow } from './api-entitlements'
export {
  useDeleteEntitlement,
  useGroupEntitlements,
  useSetGroupPlan,
  useUpsertEntitlement,
} from './api-entitlements'
export type { GroupInvitation } from './api-invitations'
export {
  useGroupInvitations,
  useResendInvitation,
  useRevokeInvitation,
} from './api-invitations'

export type GroupUpdateInput = {
  presentation_currency?: string
  intercompany_mirroring?: boolean
  retention_days?: number
  purge_grace_days?: number
}

/**
 * The companies registered under one group (`GET …/companies`). The detail
 * screen reads it; the add-company wizard works from `/platform/groups/
 * {group}/users` instead, which is why this call never existed here before
 * plans/58 Task 11.
 */
export function useGroupCompanies(groupId: string | null) {
  return useQuery({
    queryKey: ['platform', 'groups', groupId, 'companies'],
    enabled: groupId !== null,
    queryFn: async (): Promise<GroupCompany[]> => {
      const result = await api.GET('/platform/groups/{group}/companies', {
        params: { path: { group: groupId ?? '' } },
      })
      if (!result.data) throw new Error('GET /platform/groups/{group}/companies returned no body')
      return result.data.data
    },
  })
}

/**
 * The support sessions opened into one group. The queryKey matches the
 * support feature's `useGroupSessions` exactly, so both share one cache entry;
 * a feature may not import from another feature, which is why the small fetch
 * lives here too (the same precedent `usePlanCatalogue` below documents).
 */
export function useGroupSupportSessions(groupId: string | null) {
  return useQuery({
    queryKey: ['platform', 'groups', groupId, 'support-sessions'],
    enabled: groupId !== null,
    queryFn: async (): Promise<SupportSession[]> => {
      const result = await api.GET('/platform/groups/{group}/support-sessions', {
        params: { path: { group: groupId ?? '' } },
      })
      if (!result.data) {
        throw new Error('GET /platform/groups/{group}/support-sessions returned no body')
      }
      return result.data.data
    },
  })
}

/** Revoke one support session — the way back from an open grant. */
export function useRevokeSupportSession() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (sessionId: string) =>
      api.DELETE('/platform/support-sessions/{session}', {
        params: { path: { session: sessionId } },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] === 'platform' && query.queryKey.includes('support-sessions'),
      })
    },
  })
}

export function useGroupUsers(groupId: string | null, open: boolean) {
  return useQuery({
    queryKey: ['platform', 'groups', groupId, 'users'],
    enabled: open && groupId !== null,
    queryFn: async (): Promise<GroupUser[]> => {
      const result = await api.GET('/platform/groups/{group}/users', {
        params: { path: { group: groupId ?? '' } },
      })
      if (!result.data) throw new Error('GET /platform/groups/{group}/users returned no body')
      return result.data.data
    },
  })
}

/**
 * Group settings are commercial facts, so the platform endpoint is not
 * support-session gated. The generated client is regenerated after the
 * platform endpoint lands; this narrow assertion keeps the new call typed in
 * the meantime without weakening the input shape used by the form.
 */
export function useUpdateGroup() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ groupId, input }: { groupId: string; input: GroupUpdateInput }) =>
      (
        api.PATCH as unknown as (
          path: '/platform/groups/{group}',
          options: {
            params: { path: { group: string } }
            body: GroupUpdateInput
          },
        ) => Promise<unknown>
      )('/platform/groups/{group}', {
        params: { path: { group: groupId } },
        body: input,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['platform', 'groups'] })
    },
  })
}

export function useAddCompany() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ groupId, input }: { groupId: string; input: AddCompanyInput }) =>
      api.POST('/platform/groups/{group}/companies', {
        params: { path: { group: groupId } },
        body: input,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['platform', 'groups'] })
    },
  })
}

/**
 * Suspend a group. The screen confirms first — it is the step that cuts a
 * customer off — but the endpoint itself is a reversible state change: a
 * suspended group can be reactivated at any point before the purge runs
 * (design spec §6.4).
 */
export function useSuspendGroup() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (groupId: string) =>
      api.POST('/platform/groups/{group}/suspend', { params: { path: { group: groupId } } }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['platform', 'groups'] })
    },
  })
}

/**
 * Bring a suspended (or pending-purge) group back. Never offered for a purged
 * group — the API refuses that, and the screen does not render the control.
 */
export function useReactivateGroup() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (groupId: string) =>
      api.POST('/platform/groups/{group}/reactivate', { params: { path: { group: groupId } } }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['platform', 'groups'] })
    },
  })
}

/**
 * Schedule a suspended group for purge — the operator-facing half of
 * offboarding; the actual deletion runs later, on its own grace period. The
 * server refuses this for anything but a suspended group (422), which is why
 * the screen only offers the control in that state to begin with.
 */
export function useSchedulePurge() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (groupId: string) =>
      api.POST('/platform/groups/{group}/schedule-purge', {
        params: { path: { group: groupId } },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['platform', 'groups'] })
    },
  })
}

export type CataloguePlan = components['schemas']['PlanResource']

/**
 * The company limit a group sits under. Under module-based onboarding a
 * group can have no plan and still carry a `companies.max` override, so the
 * group's own `companies_max` wins when the server sends one; the plan
 * catalogue is only the fallback for a group that has neither (Task B-4).
 */
export function slotLimit(group: GroupRow, plans: CataloguePlan[]): number | null {
  const override = parseLimit(group.companies_max)
  if (override !== null) return override

  const plan = plans.find((candidate) => candidate.id === group.plan?.id)
  return parseLimit(plan?.entitlements?.find((entry) => entry.key === 'companies.max')?.value)
}

function parseLimit(raw: number | string | null | undefined): number | null {
  if (raw === undefined || raw === null) return null
  if (typeof raw === 'string' && raw.trim() === '') return null

  const value = Number(raw)
  return Number.isFinite(value) ? value : null
}

/**
 * The plan catalogue — the same endpoint the onboarding wizard reads its
 * "N of M slots" figure from: the plan's `companies.max` entitlement
 * (step-customer.tsx `planLimit()`). The groups table reuses it so both
 * screens quote the same number. The queryKey matches the plans feature's
 * `usePlans()` exactly, so the two share one cache entry and one request; a
 * feature may not import from another feature (AGENTS.md), which is why the
 * small fetch lives here too.
 */
export function usePlanCatalogue() {
  return useQuery({
    queryKey: ['platform', 'plans'],
    queryFn: async (): Promise<CataloguePlan[]> => {
      const { data } = await api.GET('/platform/plans')
      if (!data) {
        throw new Error('GET /platform/plans returned no body')
      }
      return data.data
    },
  })
}
